import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { SearchInput } from '@/components/common/SearchInput';
import type { PermissionCode } from '@/constants/permissions';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { PageTitle } from './PageTitle';
import { PageToolbar } from './PageToolbar';

export interface PageSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

export interface UpcomingPageSearch {
  placeholder: string;
  upcomingReason: string;
}

const NO_SEARCH_TEXT = '';
const SEARCH_BOX_CLASS = 'min-w-0 max-sm:order-first sm:min-w-48';
const ignoreSearchChange = () => undefined;

export interface PageCreateAction {
  label: string;
  mobileLabel?: string;
  onClick: () => void;
  requires?: PermissionCode | undefined;
}

const DEFAULT_MOBILE_CREATE_LABEL = 'Add';

interface PageHeaderProps {
  title: ReactNode;
  leading?: ReactNode;
  titleAddon?: ReactNode;
  breadcrumbAddon?: ReactNode;
  search?: PageSearch | UpcomingPageSearch | undefined;
  actions?: ReactNode;
  createAction?: PageCreateAction | undefined;
  panel?: ReactNode;
}

export function PageHeader({
  title,
  leading,
  titleAddon,
  breadcrumbAddon,
  search,
  actions,
  createAction,
  panel,
}: PageHeaderProps) {
  const { can } = usePermissions();
  if (createAction && !can(createAction.requires)) createAction = undefined;
  const hasToolbarControls = Boolean(search) || Boolean(actions) || Boolean(createAction);

  return (
    <div className="flex flex-col gap-3">
      <PageTitle leading={leading} titleAddon={titleAddon}>
        {title}
      </PageTitle>
      <PageToolbar currentTitle={title} breadcrumbAddon={breadcrumbAddon} panel={panel}>
        {hasToolbarControls ? (
          <>
            {createAction && (
              <Button onClick={createAction.onClick} size="sm" className="gap-1.5 lg:hidden">
                <Plus className="size-4" />
                {createAction.mobileLabel ?? DEFAULT_MOBILE_CREATE_LABEL}
              </Button>
            )}
            {search &&
              ('upcomingReason' in search ? (
                <SearchInput
                  value={NO_SEARCH_TEXT}
                  onChange={ignoreSearchChange}
                  placeholder={search.placeholder}
                  disabledReason={search.upcomingReason}
                  className={SEARCH_BOX_CLASS}
                />
              ) : (
                <SearchInput
                  value={search.value}
                  onChange={search.onChange}
                  placeholder={search.placeholder}
                  className={SEARCH_BOX_CLASS}
                />
              ))}
            {actions}
            {createAction && (
              <Button
                onClick={createAction.onClick}
                size="lg"
                className="hidden gap-2 px-4 lg:inline-flex"
              >
                <Plus className="size-4" />
                {createAction.label}
              </Button>
            )}
          </>
        ) : null}
      </PageToolbar>
    </div>
  );
}
